import { createSelector } from 'reselect';
import get from 'lodash/get';
import find from 'lodash/find';
import remove from 'lodash/remove';
import { getProfileName } from 'utils/artist';
import { initialState } from './reducer';
import { createSelectFieldOptions, getCityWithAreasLabel } from '../../utils/common';
import { getProfileProfession } from '../Artist/utils';

const selectAppDomain = state => state.App || initialState;

const selectUserData = () => createSelector(selectAppDomain, subState => get(subState, 'userData'));
const selectUserSettings = () => createSelector(selectAppDomain, subState => get(subState, 'settings'));

const selectUserProfiles = () => createSelector(selectAppDomain, subState => get(subState, 'profiles'));
const selectProfileIdsWithAccess = () =>
  createSelector(selectAppDomain, subState => get(subState, 'profileIdsWithAccess'));
const selectIsUserProfilesLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isProfilesLoading'));
const selectProfilePermissions = () => createSelector(selectAppDomain, subState => get(subState, 'profilePermissions'));
const selectUserPermissions = () => createSelector(selectAppDomain, subState => get(subState, 'userPermissions'));

const selectActiveProfile = () => createSelector(selectAppDomain, subState => get(subState, 'activeProfileData'));
const selecIsSwitchingProfile = () => createSelector(selectAppDomain, subState => get(subState, 'isSwitchingProfile'));
const selectActiveProfileId = () => createSelector(selectAppDomain, subState => get(subState, 'activeProfileData.id'));

const selectActiveProfileSubscriptions = () =>
  createSelector(selectAppDomain, subState => get(subState, 'activeProfileSubscriptions'));

const selectProfileSubscriptions = () =>
  createSelector(selectAppDomain, subState => get(subState, 'profileSubscriptions'));

const selectProSubscription = profileTypeId =>
  createSelector(selectAppDomain, subState => {
    const subscriptions = get(subState, 'subscriptions.data');
    return subscriptions.find(
      subscription => subscription.profileType?.id === Number(profileTypeId) && subscription.name !== 'free',
    );
  });

const selectActiveProfileInvoices = () =>
  createSelector(selectAppDomain, subState => get(subState, 'activeProfileInvoices'));

const selectSubscriptionChurnedStatus = () =>
  createSelector(selectAppDomain, subState => get(subState, 'subscriptionChurnedStatus'));

const selectLoginErrorMessage = () => createSelector(selectAppDomain, subState => get(subState, 'loginError'));
const selectLoginLoadingState = () => createSelector(selectAppDomain, subState => get(subState, 'loginLoading'));
const selectLoginAttemptsCount = () => createSelector(selectAppDomain, subState => get(subState, 'loginTries'));

const selecActiveProfileById = profileId =>
  createSelector(selectAppDomain, subState => {
    const profiles = get(subState, 'profiles');
    return find(profiles, { id: Number(profileId) });
  });

const selectArtistProfessionsOptions = () =>
  createSelector(selectAppDomain, subState => {
    const professions = [...get(subState, 'artistProfessions', [])];
    remove(professions, { id: 1 }); // remove 'singer' as we should set voice type instead of it
    return createSelectFieldOptions(professions, 'id', 'name', { type: 'profession' }, true);
  });

const selectAllArtistProfessionsOptions = () =>
  createSelector(selectAppDomain, subState => {
    const professions = get(subState, 'artistProfessions', []);
    return createSelectFieldOptions(professions, 'id', 'name', { type: 'profession' });
  });

const selectAtistProfessions = () =>
  createSelector(selectAppDomain, subState => get(subState, 'artistProfessions', []));

const selectAggregationsValues = key =>
  createSelector(selectAppDomain, subState => get(subState, `aggregations.${key}`, []));

const selectAggregationsOptions = (key, valueName = 'key', labelName = 'name', additionalProps = true) =>
  createSelector(selectAppDomain, subState => {
    const aggregationsList = get(subState, `aggregations.${key}`, []);
    return createSelectFieldOptions(aggregationsList, valueName, labelName, { type: key }, additionalProps);
  });

const selectAggregationsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'aggregationTypeLoading'));

const selectAggregationsError = () => createSelector(selectAppDomain, subState => get(subState, 'aggregations.error'));

const selectStartAggregation = () => createSelector(selectAppDomain, subState => get(subState, 'startAggregations'));

const selectAgenciesListOptions = (withAllData = false) =>
  createSelector(selectAppDomain, subState => {
    const agencies = get(subState, 'agencies', []);
    return createSelectFieldOptions(agencies, 'id', 'name', {}, withAllData);
  });

const selectAgenciesListOptionsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'agenciesLoading'));

const selectCompaniesListOptions = (withAllData = false) =>
  createSelector(selectAppDomain, subState => {
    const companies = get(subState, 'companies', []);
    return createSelectFieldOptions(companies, 'id', 'name', {}, withAllData);
  });

const selectCompaniesListOptionsLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'companiesLoading'));

const selectProfilesListOptions = () =>
  createSelector(selectAppDomain, subState => {
    const profiles = get(subState, 'profilesList', []);
    return profiles?.map(profile => {
      const profession = getProfileProfession(profile);

      return {
        ...profile,
        value: get(profile, 'id'),
        url: profile?.image?.small,
        label: `${getProfileName(profile)} ${profession ? `(${profession})` : ''}`,
      };
    });
  });

const selectProfilesListLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'profilesListLoading'));

const selectResetPasswordMessage = () =>
  createSelector(selectAppDomain, subState => get(subState, 'resetPasswordInfo'));

const selectPasswordKeyStatus = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isResetPasswordKeyActive'));

const selectPasswordUpdateStatus = () =>
  createSelector(selectAppDomain, subState => get(subState, 'isPasswordUpdated'));

const selectNotifications = () => createSelector(selectAppDomain, subState => get(subState, 'notifications', []));

const selectVoiceTypesOptions = () =>
  createSelector(selectAppDomain, subState => {
    return createSelectFieldOptions(get(subState, 'voiceTypes.data', []), 'id', 'name', {}, true);
  });

const selectCitiesOptions = () =>
  createSelector(selectAppDomain, subState => ({
    options: createSelectFieldOptions(get(subState, 'cities.data', []), 'id', getCityWithAreasLabel),
    loading: get(subState, 'cities.loading', false),
  }));

const selectVenuesOptions = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'venues.loading', false),
    options: createSelectFieldOptions(get(subState, 'venues.data', []), 'id', 'name', {}, true),
  }));

const selectVenuesSearchOptions = (withAllData = false) =>
  createSelector(selectAppDomain, subState => {
    const venueSearch = get(subState, 'venueSearch', []);
    return createSelectFieldOptions(venueSearch, 'id', 'name', {}, withAllData);
  });

const selectStages = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'stages.loading', false),
    data: get(subState, 'stages.data', []),
  }));

const selectBufferValueData = () =>
  createSelector(selectAppDomain, subState => ({
    loading: get(subState, 'bufferValue.loading', false),
    entity: get(subState, 'bufferValue.entity', []),
  }));

const selectWorkTypesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'workTypes.data', []), 'id', 'name'),
  );

const selectWorkTypesLoading = () => createSelector(selectAppDomain, subState => get(subState, 'workTypes.loading'));

const selectWorkStylesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'workStyles.data', []), 'id', 'name'),
  );

const selectBanner = () => createSelector(selectAppDomain, subState => get(subState, 'notificationBanner'));

const selectPrivacyOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'privacyOptions.data', []), 'id', 'name'),
  );

const selectPrivacyAttributes = () =>
  createSelector(selectAppDomain, subState => get(subState, 'privacyOptions.data', []));

const selectAOSProfessionsOptions = () =>
  createSelector(selectAppDomain, subState => {
    const professions = get(subState, 'aosProfessions.data', []);
    return createSelectFieldOptions(professions, 'id', 'name', {});
  });

const selectLanguages = () => createSelector(selectAppDomain, subState => get(subState, 'languages.data', false));

const selectLanguagesOptions = () =>
  createSelector(selectLanguages(), langs => createSelectFieldOptions(langs, 'id', 'name', {}, true));

const selectDynamicCountriesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, `${initialState.dynamicCountries.listName}.data`), 'id', 'name'),
  );

const selectMediaAttrTypesOptions = () =>
  createSelector(selectAppDomain, subState =>
    createSelectFieldOptions(get(subState, 'attributeTypes.data', []), 'id', 'name'),
  );

const selectMediaAttrTypes = () =>
  createSelector(selectAppDomain, subState => get(subState, 'attributeTypes.data', []));

const selectVideoFormatsNames = () =>
  createSelector(selectAppDomain, subState =>
    get(subState, `${initialState.videoFormats.listName}.data`, [])?.map(format => format?.slug),
  );

const selectSearchProductionsList = () =>
  createSelector(selectAppDomain, subState => get(subState, 'searchProductions'));

const selectSearchProductionsListLoading = () =>
  createSelector(selectAppDomain, subState => get(subState, 'searchProductions.loading'));

const selectProfileAccessRequests = () =>
  createSelector(selectAppDomain, subState => get(subState, 'profileAccessRequests'));

export {
  selectAppDomain,
  selectUserData,
  selectUserProfiles,
  selectActiveProfile,
  selectActiveProfileId,
  selecActiveProfileById,
  selectArtistProfessionsOptions,
  selectAllArtistProfessionsOptions,
  selectVoiceTypesOptions,
  selectAtistProfessions,
  selectAggregationsValues,
  selectAggregationsOptions,
  selectAgenciesListOptions,
  selectAgenciesListOptionsLoading,
  selectCompaniesListOptions,
  selectCompaniesListOptionsLoading,
  selectProfilesListOptions,
  selectResetPasswordMessage,
  selectPasswordKeyStatus,
  selectPasswordUpdateStatus,
  selectAggregationsLoading,
  selectProfilesListLoading,
  selectNotifications,
  selectLoginErrorMessage,
  selectStartAggregation,
  selectUserPermissions,
  selectProfilePermissions,
  selectAggregationsError,
  selectIsUserProfilesLoading,
  selecIsSwitchingProfile,
  selectCitiesOptions,
  selectVenuesOptions,
  selectStages,
  selectBufferValueData,
  selectWorkTypesOptions,
  selectWorkStylesOptions,
  selectBanner,
  selectLoginLoadingState,
  selectPrivacyOptions,
  selectPrivacyAttributes,
  selectAOSProfessionsOptions,
  selectLoginAttemptsCount,
  selectLanguages,
  selectLanguagesOptions,
  selectDynamicCountriesOptions,
  selectMediaAttrTypesOptions,
  selectMediaAttrTypes,
  selectVideoFormatsNames,
  selectActiveProfileSubscriptions,
  selectProfileSubscriptions,
  selectProSubscription,
  selectActiveProfileInvoices,
  selectSubscriptionChurnedStatus,
  selectProfileIdsWithAccess,
  selectSearchProductionsList,
  selectSearchProductionsListLoading,
  selectVenuesSearchOptions,
  selectUserSettings,
  selectProfileAccessRequests,
  selectWorkTypesLoading,
};
